import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import cx from '@travel/icons/_utils/classnames';
import Dialog from '@travel/traveler-core/components/Dialog';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import { FlatButton } from '@travel/ui';

import AmountInput from 'favoriteList/components/SearchForm/components/AmountInput';
import DateRow from 'favoriteList/components/SearchForm/components/DateRow';
import Form from 'favoriteList/components/SearchForm/components/Form';
import SearchEntryButton, {
  CapacityRow,
  DateRow as MobileDateRow,
} from 'favoriteList/components/SearchForm/components/SearchEntryButton';
import SearchFormContents from 'favoriteList/components/SearchForm/components/SearchFormContents';
import labels from 'favoriteList/components/SearchForm/constants/labels';
import useSearchForm from 'favoriteList/components/SearchForm/hooks/useSearchForm';
import {
  getIsProviderFetching,
  getProviderDataCount,
} from 'favoriteList/store/providerPlan/selectors';
import CommonFormValues from './CommonFormValues';

import type { URLSearchQuery } from 'SearchForm-Types';

import styles from './searchForm.module.scss';

function SearchForm(props: { query?: URLSearchQuery; className?: string; isDomestic: boolean }) {
  const { isOpen, onOpen: onOpenDialog, onClose: onCloseDialog } = useDialogHandler(false);
  const { isDomestic } = props;
  const isProvideFetching = useSelector(getIsProviderFetching);
  const providerDataCount = useSelector(getProviderDataCount);
  // To show an empty date/person/room button on SP, but still allow default values to be set once the search form modal is opened
  const [isFirstView, setIsFirstView] = useState(true);

  // will not use query for selected areas but data from api
  const form = useSearchForm(props.query, isDomestic);
  const { isSP } = useDeviceTypes();
  const previousIsSP = useRef(isSP);

  const [holidays, setHolidays] = useState([]);

  const { captureFormFields } = form;

  const onOpen = useCallback(() => {
    if (isFirstView) {
      setIsFirstView(false);
    }
    captureFormFields();
    onOpenDialog();
  }, [captureFormFields, onOpenDialog, isFirstView]);

  const onClose = useCallback(() => {
    form.resetFormFields();
    setIsFirstView(true);
    onCloseDialog();
  }, [onCloseDialog, form]);

  useEffect(() => {
    if (holidays.length) {
      return;
    }
    const _holidays = window['rtm']?.date?.holidays || null;
    if (_holidays) {
      setHolidays(_holidays);
    } else {
      const script = document.createElement('script');
      script.src =
        'https://img.travel.rakuten.co.jp/share/master/js/holidays.js?_=1727628183210?_=1727628183210';
      script.onload = () => {
        const _holidays = window['rtm']?.date?.holidays || [];
        setHolidays(_holidays);
      };
      document.head.appendChild(script);
    }
  }, [setHolidays, holidays.length]);

  useEffect(() => {
    if (previousIsSP.current !== isSP) {
      form.resetFormFields();
      setIsFirstView(true);
      previousIsSP.current = isSP;
    }
  }, [isSP, form]);

  const commonFormValues = <CommonFormValues isDomestic={isDomestic} />;

  const isSearchEnabled =
    !!form.travel.startDate && !!form.travel.endDate && !isProvideFetching && providerDataCount > 0;

  const adultCount = isFirstView ? 0 : form.travel.capacity.adults.counter;
  const childrenCount = isFirstView
    ? 0
    : form.travel.capacity.childrenUpperGrade.counter +
      form.travel.capacity.childrenLowerGrade.counter +
      form.travel.capacity.infantsBedMeal.counter +
      form.travel.capacity.infantsBed.counter +
      form.travel.capacity.infantsMeal.counter +
      form.travel.capacity.infantsNone.counter;
  const roomCount = isFirstView ? 0 : form.travel.capacity.rooms.counter;

  return (
    <div className={styles.formContainer}>
      {!isSP ? (
        <Form
          id="search"
          role="search"
          method="get"
          action={form.submitUrl}
          className={styles.form}
          onSubmit={({ query }) => {
            if (isSearchEnabled) {
              window.location.assign(form.submitUrl + '?' + query);
            }
          }}
        >
          <div className={cx(styles.horizontalRow)}>
            <DateRow
              className={cx(styles.spacing, styles.buttonOutline, styles.mainDateRow)}
              startDate={form.travel.startDate}
              endDate={form.travel.endDate}
              onDateChanged={form.travel.onChange}
              holidays={holidays}
            />
            <AmountInput
              className={cx(styles.spacing, styles.buttonOutline, styles.amountInputRow)}
              selectedCapacity={form.travel.capacity}
              isDomestic={isDomestic}
            />
            <FlatButton
              className={styles.activeButton}
              type={'submit'}
              isDisabled={!isSearchEnabled}
            >
              {labels.search}
            </FlatButton>
          </div>
          {commonFormValues}
        </Form>
      ) : (
        <>
          <SearchEntryButton className={styles.searchFormButton} onClick={onOpen}>
            <MobileDateRow
              departureDate={form.travel.startDate}
              returnDate={form.travel.endDate}
              className={styles.dateRowMobile}
            />
            <div className={styles.separator} />
            <CapacityRow
              className={cx(styles.destinationAndCapacity)}
              isTwoRowLayout={true}
              shouldHideIcon={true}
              adults={adultCount}
              children={childrenCount}
              rooms={roomCount}
            />
          </SearchEntryButton>

          <Dialog
            title={labels.accommodationScheduleNumberOfPeopleCondition}
            isOpen={isOpen}
            isUseContentFlexBox={false}
            hasEnableBack={false}
            onClose={onClose}
            contentClassName={styles.dialogWrapper}
          >
            <SearchFormContents
              form={form}
              formAction={form.submitUrl}
              query={props.query}
              holidays={holidays}
              commonFormValues={commonFormValues}
              isDomestic={isDomestic}
            />
          </Dialog>
        </>
      )}
    </div>
  );
}

export default SearchForm;
